import type { SecurityIncident } from '@kennisbank/shared';

/**
 * De klok van het incidentenregister.
 *
 * Twee termijnen lopen naast elkaar:
 *  - **72 uur richting de AP** (AVG art. 33), maar alléén zodra de
 *    privacy-contactpersoon bij triage `isDatalek: true` heeft gezet. Vóór die
 *    kwalificatie bestaat de wettelijke termijn niet en zou een rode countdown
 *    op elke phishingmail alleen maar afstompen (plan §3).
 *  - **24 uur tot triage** als interne norm uit de meldketen (plan §7c). Die
 *    geldt voor élke melding, ook eentje die uiteindelijk geen datalek blijkt.
 */

export const AP_TERMIJN_MS = 72 * 60 * 60 * 1000;
export const TRIAGE_TERMIJN_MS = 24 * 60 * 60 * 1000;

/**
 * Zomertijd-afweging.
 *
 * `datumOntdekt` is een kale dag (JJJJ-MM-DD) zonder tijdstip; het schema legt
 * vast dat de klok loopt vanaf 00:00 NL-tijd van die dag, dus in het voordeel
 * van de melder. Twee valkuilen:
 *
 * 1. `new Date('2026-10-25')` is middernacht **UTC**, en `new Date(2026, 9, 25)`
 *    is middernacht in de tijdzone van de laptop. Beide zijn fout zodra de
 *    behandelaar niet toevallig in NL zit. Daarom bepalen we de werkelijke
 *    UTC-instant van middernacht in Europe/Amsterdam via `Intl`, zonder
 *    dependency.
 * 2. De 72 uur is een **absolute duur**, geen wandkloktijd. Over de
 *    overgang naar wintertijd duurt "drie dagen later, zelfde tijdstip" 73 uur
 *    en over de zomertijdovergang 71 uur. Wij tellen daarom 72 × 3600 × 1000 ms
 *    op bij een absolute instant. Bij een lek dat op 24 oktober wordt ontdekt
 *    verloopt de termijn dus om 23:00 NL-tijd op de 27e, niet om middernacht.
 *    Dat is de strengere en juridisch juiste lezing.
 */
const NL_PARTS = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Amsterdam',
  hour12: false,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

/** Offset (ms) van Europe/Amsterdam ten opzichte van UTC op moment `at`. */
function nlOffsetMs(at: number): number {
  const parts = Object.fromEntries(
    NL_PARTS.formatToParts(new Date(at)).map((p) => [p.type, p.value]),
  );
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    // Sommige engines geven "24" voor middernacht bij hour12: false.
    Number(parts.hour) % 24,
    Number(parts.minute),
    Number(parts.second),
  );
  return asUtc - at;
}

/** UTC-instant van 00:00 NL-tijd op een JJJJ-MM-DD-datum, of null bij onzin. */
export function nlMiddernacht(isoDate: string): number | null {
  const naiefUtc = Date.parse(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(naiefUtc)) return null;
  // Tweede pas: de offset die geldt op het lokale moment zelf. In NL valt
  // middernacht nooit in het DST-gat (dat ligt op 02:00/03:00), dus dit
  // convergeert na één correctie.
  const eerste = naiefUtc - nlOffsetMs(naiefUtc);
  return naiefUtc - nlOffsetMs(eerste);
}

/** Moment waarop de 72-uurstermijn richting de AP verloopt. */
export function apDeadline(datumOntdekt: string): number | null {
  const start = nlMiddernacht(datumOntdekt);
  return start === null ? null : start + AP_TERMIJN_MS;
}

export type TermijnStatus =
  /** Nog geen triage: de interne meldketen loopt. */
  | { soort: 'wacht_op_triage'; wachtMs: number; teLaat: boolean }
  /** Beoordeeld als geen datalek: geen wettelijke klok. */
  | { soort: 'geen_datalek' }
  /** Afgerond zonder datalek-kwalificatie. */
  | { soort: 'afgerond' }
  /** Datalek, maar de beheerder motiveerde dat melden aan de AP niet nodig is. */
  | { soort: 'geen_meldplicht' }
  /** Datalek, gemeld aan de AP: klok gestopt. */
  | { soort: 'ap_gemeld'; op: string; opTijd: boolean | null }
  /** Datalek, klok loopt. */
  | { soort: 'loopt'; resterendMs: number; urgent: boolean; deadline: number }
  /** Datalek, 72 uur verstreken zonder AP-melding. */
  | { soort: 'overschreden'; overtijdMs: number; deadline: number }
  /** Datum onleesbaar (zou niet mogen: het schema dwingt JJJJ-MM-DD af). */
  | { soort: 'onbekend' };

export function termijnStatus(incident: SecurityIncident, nu: number): TermijnStatus {
  const triage = incident.triage;

  if (triage?.isDatalek === true) {
    const deadline = apDeadline(incident.melderOpgave.datumOntdekt);
    if (deadline === null) return { soort: 'onbekend' };

    if (triage.apGemeldOp) {
      const gemeld = Date.parse(triage.apGemeldOp);
      return {
        soort: 'ap_gemeld',
        op: triage.apGemeldOp,
        opTijd: Number.isNaN(gemeld) ? null : gemeld <= deadline,
      };
    }
    // Expliciet "geen meldplicht" stopt de escalatie, net als in de
    // deadline-janitor (plan §7c). Alleen expliciet: `undefined` telt niet.
    if (triage.apMeldingNodig === false) return { soort: 'geen_meldplicht' };

    const resterend = deadline - nu;
    if (resterend <= 0) return { soort: 'overschreden', overtijdMs: -resterend, deadline };
    return {
      soort: 'loopt',
      resterendMs: resterend,
      urgent: resterend <= 24 * 60 * 60 * 1000,
      deadline,
    };
  }

  if (triage?.isDatalek === false) return { soort: 'geen_datalek' };

  if (incident.status === 'afgerond') return { soort: 'afgerond' };

  const binnen = Date.parse(incident.createdAt);
  if (Number.isNaN(binnen)) return { soort: 'onbekend' };
  const wacht = nu - binnen;
  return { soort: 'wacht_op_triage', wachtMs: wacht, teLaat: wacht > TRIAGE_TERMIJN_MS };
}

/** "2 dagen 5 uur" / "18 uur 12 min" / "45 min". Altijd naar beneden afgerond. */
export function formatDuur(ms: number): string {
  const totaalMin = Math.max(0, Math.floor(ms / 60_000));
  const uren = Math.floor(totaalMin / 60);
  const min = totaalMin % 60;
  if (uren >= 48) {
    const dagen = Math.floor(uren / 24);
    const restUren = uren % 24;
    return restUren > 0 ? `${dagen} dagen ${restUren} uur` : `${dagen} dagen`;
  }
  if (uren >= 1) return min > 0 ? `${uren} uur ${min} min` : `${uren} uur`;
  return `${min} min`;
}

export function formatMoment(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('nl-NL');
}

export function formatDatum(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * Vraag 1 mag "anoniem" zijn. Herkennen we zoiets, dan tonen we géén
 * herleidbare gebruikersgegevens in het scherm — `submittedByUserId` bestaat
 * voor de audit trail, niet voor de behandelaar (plan §11 besluit 3).
 * Bewust ruim: bij twijfel verbergen we liever te veel dan te weinig.
 */
const ANONIEM_EXACT = new Set([
  'anoniem',
  'anonymous',
  'anon',
  'onbekend',
  'geen',
  'geen naam',
  'naamloos',
  'nvt',
  'n.v.t.',
  'niet van toepassing',
  'x',
  'xx',
  'xxx',
  '-',
  '--',
  '.',
  '?',
]);

export function isAnoniemGemeld(melderNaam: string): boolean {
  const naam = melderNaam.trim().toLowerCase().replace(/\s+/g, ' ');
  if (naam.length === 0) return true;
  if (ANONIEM_EXACT.has(naam)) return true;
  return naam.includes('anoniem') || naam.includes('anonymous');
}
